// What a practice asks of you, read out of the corpus's free-text burden line.
//
// The corpus writes prose, and 135 dossiers wrote it 1,127 different ways
// (audited 29 Jul 2026): "10 min · free", "Ongoing background change, free",
// "under 2 minutes · low cost (a roller)", "one-time · $40", "free", "none".
// Three separate facts hide in there, and every surface needs them apart:
//
//   time   how long one go takes, or what kind of ask it is when no clock fits
//   cost   where it sits on saves / free / low / moderate / high, or that it varies
//   notes  what neither slot holds: equipment, difficulty, a dollar figure
//
// So this reads by scanning, not by splitting on the dot. The corpus puts the
// two slots in either order, separates them with a dot or a comma, and often
// leaves one out: 167 practices wrote "free" alone, which the old
// first-segment-is-the-time reading rendered as a duration. Position tells you
// nothing here. The words do.
//
// Nothing is inferred that the text does not say. An unstated cost stays null
// rather than becoming "free", because a research gap is not a discount, and
// the engine treats the two differently.

export type CostLevel = "saves" | "free" | "low" | "moderate" | "high" | "varies";

// What the ask is, when there is no clock on it. "span" is a commitment
// length ("21 days", "3 months"), which is deliberately not minutes: folding
// it into the day's estimate would put a quarter of an hour into Today.
export type BurdenShape =
  | "duration"
  | "none"
  | "one-time"
  | "ongoing"
  | "appointment"
  | "span"
  | "varies"
  | "unknown";

export interface Burden {
  /** Minutes for one go, low and high. null when the text states no clock time. */
  minutes: { low: number; high: number } | null;
  shape: BurdenShape;
  /** The highest point on the scale the text names: what it might cost you, not the best case. */
  cost: CostLevel | null;
  /** Requirements, difficulty, caveats. Practice modal only; too long for a row. */
  notes: string[];
  /** Canonical time label, "Under 2 min" / "One-time" / "Ongoing". "" when unstated. */
  time: string;
  /** Canonical cost label, "Free" / "Low cost" / "Free to low cost". "" when unstated. */
  money: string;
}

const EMPTY: Burden = { minutes: null, shape: "unknown", cost: null, notes: [], time: "", money: "" };

// Bookkeeping the corpus writes where a value would go: the researcher looked
// and there was nothing to carry. It is not display copy, and it is not zero.
const SENTINEL =
  /^(?:n\/a|not applicable\b.*|not carried\b.*|not specified\b.*|not stated\b.*|none stated|unknown|claim and link only|record only|reading|(?:cost|price|no price|no figure)\b.*not (?:stated|specified|carried).*|the source\b.*|daily|nightly|weekly|monthly|periodic|seasonal|every morning|every meal)$/;

function capFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Segments, not slots. Commas count as separators (210 dossiers used one where
// the dot belongs), but never a comma inside a number.
function segments(lower: string): string[] {
  return lower
    .replace(/\b(?:variable|varies for)? ?time and cost (?:both )?(?:vary|varies)\b|\bvariable time and cost\b/, "varies · varies")
    .replace(/^variable time$/, "variable")
    .split(/·|;|\/(?=\s)|,(?!\d)|\band\b(?=\s+(?:free|low|moderate|high|saves|no |it saves))/)
    .map((s) => s.trim().replace(/^(?:and|then|plus|or)\s+/, "").trim())
    .filter(Boolean);
}

// "Varies" on its own could be the clock or the price, and the corpus uses it
// for both. One token states one fact, so it is assigned once, after everything
// certain has been placed: it fills the time when nothing else did, and falls
// through to the cost when the time is already known.
const AMBIGUOUS = /^(?:varies|variable|test-dependent|it varies)$/;

const NUM = String.raw`\d+(?:\.\d+)?`;
const MINUTE_UNITS: Record<string, number> = {
  sec: 1 / 60, secs: 1 / 60, second: 1 / 60, seconds: 1 / 60,
  min: 1, mins: 1, minute: 1, minutes: 1,
  h: 60, hr: 60, hrs: 60, hour: 60, hours: 60,
};
const CLOCK = new RegExp(
  String.raw`(?:(under|about|approx\.?|approximately|around|up to|less than|as little as|at least|more than|over)\s+)?` +
    String.raw`(${NUM})\s*(?:(?:to|-|–|or)\s*(${NUM})\s*)?(\+|or more|or longer)?\s*` +
    String.raw`(secs?|seconds?|mins?|minutes?|hrs?|hours?|h)\b`,
);
const SPAN = new RegExp(
  String.raw`(?:(${NUM})|\b(?:one|a|an))\s*(?:(?:to|-|–)\s*(${NUM})\s*)?(days?|weeks?|months?|years?)\b`,
);

// Qualifiers worth keeping on the label because they change what the number
// counts ("under 2 min per product"). "per day" and "daily" are dropped: the
// cadence line already says how often, and saying it twice reads as a mistake.
const PER = /\b(?:per|each|a)\s+(product|item|test|check|entry|decision|session|study|muscle|meal|serving|person|visit|night|side|leg|room|window|appointment|set|dose|week|month|year)s?\b/;
// "1 hour weekly" counts the same way "1 hour per week" does.
const EVERY: Array<[RegExp, string]> = [
  [/\bweekly\b/, "week"],
  [/\bmonthly\b/, "month"],
  [/\b(?:yearly|annually)\b/, "year"],
];
const PREP = /\b(prep|preparation|setup|set-up|reading)\b/;

// Time words with no number on them. Order matters: the first hit wins, so
// the more specific phrasings sit above the bare ones.
const TIME_WORDS: Array<[RegExp, BurdenShape, string]> = [
  [/^(?:0|no|zero)\s*(?:min|mins|minutes?)?$|^no (?:extra |added |additional )?time|^within (?:daily|normal) activit|^no time/, "none", "No extra time"],
  [/^none\b/, "none", "No extra time"],
  [/\bongoing\b|^routine$|^standing\b|^continuous/, "ongoing", "Ongoing"],
  [/^one[- ]time\b|^once\b|^one[- ]off\b|^single (?:decision|purchase|swap)/, "one-time", "One-time"],
  [/\b(?:appointment|visit|consultation|clinical discussion|questionnaire)\b/, "appointment", "Appointment"],
  [/\b(?:lab test|blood test|lab work|blood draw|imaging)\b/, "appointment", "Lab test"],
  [/\bconversation\b|^one question$|^questionnaire$|^clinical conversation$/, "appointment", "One conversation"],
  [/^minutes$|^a few minutes$/, "duration", "A few minutes"],
  // An empty label keeps the segment's own words: "minutes to hours" says
  // something "Varies" does not.
  [/^(?:seconds|minutes|hours|days) to (?:minutes|hours|days|weeks)$/, "varies", ""],
  [/^seconds$/, "duration", "Seconds"],
  // A bare unit with no count: "hours", "months". Its own words, and out of the
  // day's arithmetic, because how many is exactly what is missing.
  [/^(?:hours?|days?|weeks?|months?|years?)$/, "span", ""],
  [/^(?:variable|varies|test-dependent|as needed|days to weeks|weeks to months|sessions?|calls or messages)\b/, "varies", "Varies"],
  [/^(?:planning|meal planning|calculation|calculations|shopping|grocery shopping)$/, "one-time", "One-time"],
  [/^(?:swap|substitution|one-time swap)$/, "one-time", "One-time"],
];

interface TimeHit {
  minutes: { low: number; high: number } | null;
  shape: BurdenShape;
  label: string;
}

function readTime(seg: string): TimeHit | null {
  const clock = CLOCK.exec(seg);
  if (clock) {
    const [, qual, lo, hi, plus, unit = "min"] = clock;
    const mult = MINUTE_UNITS[unit] ?? 1;
    const low = Number(lo) * mult;
    const high = hi ? Number(hi) * mult : low;
    // Over eight hours is not a task, it is a fast or a stay ("24 hours",
    // "13 to 72 hours"): it keeps its wording and stays out of the arithmetic.
    if (high > 480) return { minutes: null, shape: "span", label: capFirst(seg) };
    // "0 min" is the same statement as "no extra time", written by the number.
    if (high === 0) return { minutes: { low: 0, high: 0 }, shape: "none", label: "No extra time" };
    // A range is stated low end first even where the source did not ("150 or
    // 75 minutes", the moderate-or-vigorous exercise guideline).
    const [lowM, highM] = low <= high ? [low, high] : [high, low];
    // Read in the unit that keeps the number small: "30 sec", "45 min", "6 h",
    // never "360 min". Hours have to divide cleanly, or the reading gets worse
    // rather than better: "75 to 150 min" is the exercise guideline people
    // know, and "1.3 to 2.5 h" is nobody's sentence.
    const cleanHours = lowM >= 60 && lowM % 30 === 0 && highM % 30 === 0;
    const unitWord = highM < 1 ? "sec" : highM < 120 || !cleanHours ? "min" : "h";
    const factor = unitWord === "sec" ? 60 : unitWord === "h" ? 1 / 60 : 1;
    const round = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
    const range = lowM === highM ? round(highM * factor) : `${round(lowM * factor)} to ${round(highM * factor)}`;
    // The bound is part of the fact: "under 2 min" and "over 60 min" are
    // promises of opposite kinds, and dropping the word left both reading flat.
    const bound = qual && /^(under|less than|up to)$/.test(qual)
      ? "Under "
      : plus || (qual && /^(more than|over|at least)$/.test(qual))
        ? "Over "
        : "";
    const per = PER.exec(seg) ?? EVERY.find(([re]) => re.test(seg));
    const prep = !per && PREP.exec(seg);
    const tail = per ? ` per ${per[1]}` : prep ? ` ${prep[1]}` : "";
    return {
      minutes: { low: lowM, high: highM },
      shape: "duration",
      label: `${bound}${range} ${unitWord}${tail}`,
    };
  }
  const span = SPAN.exec(seg);
  if (span) {
    const [, lo = "1", hi, unit = "days"] = span;
    const plural = Number(hi ?? lo) === 1 ? unit.replace(/s$/, "") : unit.endsWith("s") ? unit : `${unit}s`;
    return { minutes: null, shape: "span", label: `${lo}${hi ? ` to ${hi}` : ""} ${plural}` };
  }
  for (const [re, shape, label] of TIME_WORDS) {
    if (re.test(seg)) {
      return { minutes: shape === "none" ? { low: 0, high: 0 } : null, shape, label: label || capFirst(seg) };
    }
  }
  return null;
}

const LEVELS: CostLevel[] = ["saves", "free", "low", "moderate", "high"];
const rank = (c: CostLevel) => LEVELS.indexOf(c);

// A dollar figure says the amount but not the tier, so the tier comes from the
// figure. The steps are the ones the labels already imply: pocket money, a
// real purchase, an investment.
function levelForAmount(n: number): CostLevel {
  return n <= 25 ? "low" : n <= 150 ? "moderate" : "high";
}

// "low to moderate cost" names two tiers with the word "cost" said once, so
// neither tier's own pattern sees it.
const COST_RANGE = /\b(free|low|moderate|high)\s+to\s+(free|low|moderate|high)\b/;

const COST_WORDS: Array<[RegExp, CostLevel]> = [
  [/\bsaves? (?:money|cost|you money)|\bcheaper\b|\bsaves an ongoing|\bmay save cost|\bsaves a step/, "saves"],
  [/\bfree\b|^no (?:added |extra |additional )?cost$|\bno cost\b|\bcosts? nothing\b|\bcost neutral\b|\bno purchase required\b/, "free"],
  [/\blow cost\b|\blow-cost\b|\blow \w+ cost\b|\bunder \$|^low$/, "low"],
  [/\bmoderate cost\b|\bmoderate \w+ cost\b|\bmoderate\b/, "moderate"],
  [/\bhigh cost\b|\bhigh \w+ cost\b|\bhigh up-front\b|^high$/, "high"],
  // Named but untiered: the text says money changes hands and leaves the
  // amount to the reader's circumstances.
  [
    /\bvaries\b|\bvariable\b|\bdepends on\b|\bby (?:insurance|cover|health system)\b|\bcost of (?:an appointment|a visit|care)\b|\bprescription\b|\bsubscription\b|\brecurring purchase\b|\b(?:grocery|food|product|lab|test|imaging|treatment|equipment|gym|membership|energy|clinician|consult|appointment|visit|care|session|kit) cost\b|\bbilled\b|\bpaid\b/,
    "varies",
  ],
];

// What the practice needs of you that is not money or minutes: kit, a clinician,
// a real change of habit. These read as cost because they sit in the cost slot,
// but "low cost · requires equipment" is low cost, so they stay notes and leave
// the tier alone.
const REQUIREMENT =
  /^(?:often )?(?:not )?covered\b|^(?:requires?|needs?|optional|clinician-supervised|institutional|record only|reading)\b|^gym (?:access|or equipment|or bands)|^(?:major lifestyle|moderate behavio|easy starting point|high difficulty|no equipment|intensive)|^(?:weights|a blender|equipment)$/;

// A caveat hung off a cost: "free at the minimal version", "high cost if
// bought as a kit". The tier is the tier; the condition is worth keeping too.
const CAVEAT = /\b(?:with|if|at the|after|though|unless|when|while|otherwise|for the|plus)\b.+$/;

interface CostHit {
  levels: CostLevel[];
  /** The segment as written, kept when it carries a figure worth showing. */
  literal: string | null;
  /** A condition on the tier, kept for the modal. */
  caveat: string | null;
}

function readCost(seg: string): CostHit | null {
  if (/\b(?:cost|price)\b.*\bnot (?:stated|specified|carried)\b|\bno (?:price|figure) stated\b/.test(seg)) return null;
  if (REQUIREMENT.test(seg)) return null;
  const levels: CostLevel[] = [];
  let literal: string | null = null;
  const amounts = [...seg.matchAll(/(?:\$|usd\s*)(\d+(?:[.,]\d+)?)/g)].map((m) => Number((m[1] ?? "0").replace(",", "")));
  if (amounts.length) {
    levels.push(levelForAmount(Math.max(...amounts)));
    // The figure is worth quoting, the time word in front of it is not: the
    // time slot already shows that half ("one-time approx. $25").
    literal = seg
      .replace(/^.*?(?=\b(?:\$|usd|free|low|moderate|high|saves|under|over|commonly|approx))/, "")
      .replace(/\busd\b/g, "USD");
  }
  const spread = COST_RANGE.exec(seg);
  if (spread?.[1] && spread[2]) levels.push(spread[1] as CostLevel, spread[2] as CostLevel);
  for (const [re, level] of COST_WORDS) {
    if (re.test(seg)) levels.push(level);
  }
  // Any other named cost ("maintenance cost", "remediation cost") is money
  // without a tier. Last, so it never outranks a tier the text did name.
  if (!levels.length && /\b\w+ costs?\b/.test(seg)) levels.push("varies");
  if (!levels.length) return null;
  const caveat = literal ? null : CAVEAT.exec(seg);
  return { levels, literal, caveat: caveat && caveat[0].split(" ").length > 1 ? caveat[0] : null };
}

const MONEY_LABEL: Record<CostLevel, string> = {
  saves: "Saves money",
  free: "Free",
  low: "Low cost",
  moderate: "Moderate cost",
  high: "High cost",
  // "Cost varies" not "Varies": beside a time on the metadata line, the bare
  // word left the reader to guess which of the two it was talking about.
  varies: "Cost varies",
};

// A range reads as one phrase, so the word "cost" lands once at the end:
// "Free to low cost", never "Free to low cost cost".
const MONEY_ADJ: Record<CostLevel, string> = {
  saves: "saves money",
  free: "free",
  low: "low",
  moderate: "moderate",
  high: "high",
  varies: "varies",
};

// The tier and its label, from every cost word a line turned up.
function money_(levels: CostLevel[], literals: string[]): { cost: CostLevel | null; money: string } {
  // The highest tier named, because that is what the reader might actually
  // pay. Saving money beats free and says more, so it wins over it; anything
  // that actually charges wins over both.
  const positive = levels.filter((l) => l !== "saves");
  const top = positive.length ? positive.reduce((a, b) => (rank(a) >= rank(b) ? a : b)) : null;
  const cost: CostLevel | null = levels.includes("varies") && !literals.length
    ? "varies"
    : levels.includes("saves") && (top === null || top === "free")
      ? "saves"
      : top;

  // The range is worth keeping ("Free to low cost" says something "Low cost"
  // does not), and a figure is worth keeping verbatim.
  const scale = positive.filter((l) => l !== "varies");
  const low = scale.length ? scale.reduce((a, b) => (rank(a) <= rank(b) ? a : b)) : null;
  if (literals.length) return { cost, money: capFirst(literals.join(", ")) };
  if (low && cost && low !== cost && cost !== "varies" && cost !== "saves") {
    return { cost, money: capFirst(`${MONEY_ADJ[low]} to ${MONEY_ADJ[cost]} cost`) };
  }
  return { cost, money: cost ? MONEY_LABEL[cost] : "" };
}

export function parseBurden(raw: string | null | undefined): Burden {
  const text = (raw ?? "").trim();
  if (!text) return EMPTY;
  const lower = text.toLowerCase();
  if (SENTINEL.test(lower)) return EMPTY;

  let time: TimeHit | null = null;
  const levels: CostLevel[] = [];
  const literals: string[] = [];
  const notes: string[] = [];

  let ambiguous = 0;
  for (const seg of segments(lower)) {
    if (SENTINEL.test(seg)) continue;
    if (AMBIGUOUS.test(seg)) {
      ambiguous++;
      continue;
    }
    if (REQUIREMENT.test(seg)) {
      notes.push(seg);
      continue;
    }
    // Both readings run on every segment. One segment can hold both facts
    // ("ongoing subscription or recurring purchase" is a standing ask that
    // costs something), and cost words turn up in the leading slot often
    // enough that reading them as a duration was the visible bug.
    const cost = readCost(seg);
    if (cost) {
      levels.push(...cost.levels);
      if (cost.literal) literals.push(cost.literal);
      if (cost.caveat) notes.push(cost.caveat);
      // A cost segment can also carry a bracketed aside; the aside is a note.
      const aside = /\(([^)]+)\)/.exec(seg)?.[1];
      if (aside && !/\$/.test(aside) && !readCost(aside)) notes.push(aside);
    }
    const hit = readTime(seg);
    if (hit && !time) time = hit;
    if (!hit && !cost) notes.push(seg.replace(/\(([^)]+)\)/, "$1"));
  }
  for (let i = 0; i < ambiguous; i++) {
    if (!time) time = { minutes: null, shape: "varies", label: "Varies" };
    else levels.push("varies");
  }

  const { cost, money } = money_(levels, literals);

  return {
    minutes: time?.minutes ?? null,
    shape: time?.shape ?? "unknown",
    cost,
    notes: [...new Set(notes)].map(capFirst),
    time: time?.label ?? "",
    money,
  };
}

// ---------------------------------------------------------------- the fields
//
// Three named fields, not one line (29 Jul 2026, Rob's call): a researcher
// fills `time`, `cost` and `requires` separately, so a missing cost is a
// missing field rather than a sentence to un-mix, and no cost can ever land
// where a duration goes. The scanner above stays for two jobs: migrating the
// old single field, and reading any dossier that still carries one.

export interface BurdenFields {
  /** One value from the time vocabulary: "10 min", "one-time", "ongoing". */
  time: string | null;
  /** One value from the cost vocabulary: "free", "low cost", "$40". */
  cost: string | null;
  /** What neither field holds: "gym access", "requires equipment". */
  requires: string[];
}

export const EMPTY_FIELDS: BurdenFields = { time: null, cost: null, requires: [] };

// The only place that knows the columns are called burden_time and friends.
// Every route shipping a card to the client nests them through this, because
// the one that did not (the modal's, 29 Jul 2026) shipped flat columns under a
// type that promised the object, and an absent field reads exactly like an
// empty one: the metadata line lost its time and cost in silence, and typecheck
// had nothing to check.
export function burdenFieldsOf(row: {
  burdenTime: string | null;
  burdenCost: string | null;
  burdenRequires: string[];
}): BurdenFields {
  return { time: row.burdenTime, cost: row.burdenCost, requires: row.burdenRequires };
}

// Each field holds one fact, so reading is unambiguous where the single line
// never was: no slot to guess, no comma to interpret, no bare "varies" that
// could be either. The label logic is shared with the scanner so both shapes
// render identically while dossiers are being migrated.
export function readBurden(f: BurdenFields | null | undefined): Burden {
  if (!f) return EMPTY;
  const t = f.time?.trim().toLowerCase();
  const c = f.cost?.trim().toLowerCase();
  const time = t && !SENTINEL.test(t) ? readTime(t) : null;
  const hit = c && !SENTINEL.test(c) ? readCost(c) : null;
  const { cost, money } = money_(hit?.levels ?? [], hit?.literal ? [hit.literal] : []);
  // A caveat the cost field carried with it ("free at the minimal version")
  // joins the requirements; both are modal copy.
  const notes = [...f.requires, ...(hit?.caveat ? [hit.caveat] : [])];
  return {
    minutes: time?.minutes ?? null,
    shape: time?.shape ?? "unknown",
    cost,
    notes: [...new Set(notes.map(capFirst))],
    time: time?.label ?? "",
    money,
  };
}

// The old single line, split into the three fields it was always holding. This
// is what migrated the corpus and what the importer falls back to; the round
// trip through readBurden is asserted by the unit suite.
export function splitBurden(raw: string | null | undefined): BurdenFields {
  const b = parseBurden(raw);
  const down = (p: string) => p.charAt(0).toLowerCase() + p.slice(1);
  return {
    time: b.time ? down(b.time) : null,
    cost: b.money ? down(b.money) : null,
    requires: b.notes.map(down),
  };
}

// The engine's two questions. Both are strict: an unstated cost answers no to
// each, so a dossier that never mentioned money neither earns the free bonus
// nor takes the paid penalty.
export function burdenIsFree(cost: CostLevel | null): boolean {
  return cost === "free" || cost === "saves";
}

export function burdenCostsMoney(cost: CostLevel | null): boolean {
  return cost === "low" || cost === "moderate" || cost === "high" || cost === "varies";
}

// Minutes for one go, for the day's estimate: the midpoint of a range, and a
// stated fallback when the practice puts no clock on itself at all.
export function burdenMinutes(minutes: { low: number; high: number } | null, fallback = 5): number {
  return minutes ? (minutes.low + minutes.high) / 2 : fallback;
}
